import React from "react";

import { A } from "./strings";
import { RefreshCheckResult } from "../../Store/Reducers/AccountPlanAccessSlice";
import { smallButton } from "../AdminAccountPlansTemplateContainer/parts";

/** The refresh intervals an Izba admin can pick (days). */
export const REFRESH_DAY_OPTIONS = [1, 7, 14, 30, 60, 90];

/** The server's default when an organization has never been set. */
export const DEFAULT_REFRESH_DAYS = 30;

const shortDate = (iso: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

/** "3 accounts checked · 1 overdue: Acme Corp (v2, published Oct 1, due Oct 31) · 1 bell sent" */
export const checkResultText = (result: RefreshCheckResult): string =>
  A.checkResult(
    result.checked,
    result.overdue.map(o =>
      A.overdueAccount(
        o.account_name,
        o.published_plan_version,
        shortDate(o.published_at),
        shortDate(o.due_at)
      )
    ),
    result.notified
  );

interface Props {
  orgName: string;
  schema: string;
  days: number;
  disabled: boolean;
  checking: boolean;
  result: RefreshCheckResult | undefined;
  /**
   * The value picked and waiting on its confirm (F-UF-F27): the select shows it
   * instead of snapping back to the saved value while the confirm is open.
   */
  picked?: number | null;
  /** A new value was picked; the parent asks to confirm before saving. */
  onPick: (days: number) => void;
  onCheck: () => void;
}

/** "Plan refresh: every [N] days" plus "Check now" and its last result. */
const PlanRefreshCell: React.FC<Props> = ({
  orgName,
  schema,
  days,
  disabled,
  checking,
  result,
  picked = null,
  onPick,
  onCheck,
}) => {
  const selectId = `refresh-days-${schema}`;
  const shown = picked ?? days;
  const options = REFRESH_DAY_OPTIONS.includes(days)
    ? REFRESH_DAY_OPTIONS
    : [...REFRESH_DAY_OPTIONS, days].sort((a, b) => a - b);
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-1.5 text-sm text-gray-700">
        <label htmlFor={selectId}>{A.refreshLabel}</label>
        <select
          id={selectId}
          aria-label={A.refreshSelect(orgName)}
          value={shown}
          disabled={disabled}
          onChange={e => {
            const next = Number(e.target.value);
            if (next !== shown) onPick(next);
          }}
          className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm focus:border-blue-500 focus:outline-none disabled:opacity-50"
        >
          {options.map(n => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <span>{A.refreshDaysUnit}</span>
      </div>
      <button
        type="button"
        className={smallButton}
        aria-label={A.checkNowLabel(orgName)}
        disabled={checking}
        onClick={onCheck}
      >
        {checking ? A.checking : A.checkNow}
      </button>
      {result && (
        <div
          role="status"
          data-testid={`refresh-result-${schema}`}
          className="text-xs text-gray-600"
        >
          {checkResultText(result)}
        </div>
      )}
    </div>
  );
};

export default PlanRefreshCell;
